/**
 * =============================================================================
 * CASINO SAAS PLATFORM - TYPESCRIPT TYPE DEFINITIONS
 * =============================================================================
 * 
 * This file contains strict TypeScript interfaces for:
 * 1. Database models (mirroring D1 schema)
 * 2. API request/response payloads
 * 3. RBAC permissions and roles
 * 4. Configuration types
 * 
 * All types are designed to be:
 * - Self-documenting with JSDoc comments
 * - Strict (no 'any' types)
 * - Compatible with Cloudflare Workers environment
 * 
 * =============================================================================
 */

// Cloudflare Workers type definitions
/// <reference types="@cloudflare/workers-types" />

// =============================================================================
// ENUMS
// =============================================================================

/**
 * User roles in hierarchical order (highest to lowest)
 * Flow: SAAS_OWNER → CASINO_MANAGER → MANAGER → AGENT → PLAYER
 */
export enum UserRole {
  SAAS_OWNER = 'SAAS_OWNER',
  CASINO_MANAGER = 'CASINO_MANAGER',
  MANAGER = 'MANAGER',
  AGENT = 'AGENT',
  PLAYER = 'PLAYER'
}

/**
 * Supported ERC-20 token types for crypto wallets
 */
export enum TokenType {
  ETH = 'ETH',
  USDC = 'USDC',
  USDT = 'USDT'
}

/**
 * Transaction categories
 */
export enum TransactionCategory {
  DEPOSIT = 'deposit',
  WITHDRAWAL = 'withdrawal',
  BET = 'bet',
  COMMISSION = 'commission',
  TRANSFER = 'transfer',
  BONUS = 'bonus',
  FEE = 'fee'
}

/**
 * Transaction direction
 */
export enum TransactionDirection {
  CREDIT = 'credit',
  DEBIT = 'debit'
}

/**
 * Payment methods
 */
export enum PaymentMethod {
  CRYPTO_ONCHAIN = 'crypto_onchain',
  CRYPTO_CUSTODIAL = 'crypto_custodial',
  CASH = 'cash'
}

/**
 * Transaction status
 */
export enum TransactionStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
  MANUAL_REVIEW = 'manual_review'
}

/**
 * Manual withdrawal queue reasons
 */
export enum ManualQueueReason {
  SAAS_AUTO_DISABLED = 'saas_auto_disabled',
  CASINO_AUTO_DISABLED = 'casino_auto_disabled',
  AMOUNT_THRESHOLD = 'amount_threshold',
  FLAGGED_ACTIVITY = 'flagged_activity',
  KYC_REQUIRED = 'kyc_required'
}

/**
 * Manual queue processing status
 */
export enum ManualQueueStatus {
  PENDING = 'pending',
  IN_REVIEW = 'in_review',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  COMPLETED = 'completed'
}

/**
 * Bet status
 */
export enum BetStatus {
  PENDING = 'pending',
  WON = 'won',
  LOST = 'lost',
  CANCELLED = 'cancelled',
  REFUNDED = 'refunded'
}

/**
 * Cash transaction types in the ledger
 */
export enum CashTransactionType {
  TOPUP = 'topup',
  DISTRIBUTION = 'distribution',
  BET_PLACEMENT = 'bet_placement',
  BET_SETTLEMENT = 'bet_settlement',
  WITHDRAWAL = 'withdrawal',
  DEPOSIT = 'deposit'
}

/**
 * Crypto transaction types in the ledger
 */
export enum CryptoTransactionType {
  DEPOSIT = 'deposit',
  WITHDRAWAL = 'withdrawal',
  BET_SETTLEMENT = 'bet_settlement',
  BONUS = 'bonus'
}

/**
 * API key types for multi-account provisioning
 */
export enum ApiKeyType {
  SAAS_OWNER = 'saas_owner',
  TENANT_CF_ACCOUNT = 'tenant_cf_account'
}

// =============================================================================
// DATABASE MODELS
// =============================================================================

/**
 * Tenant (Casino) model - represents a casino brand/tenant in the platform
 */
export interface Tenant {
  id: string;
  name: string;
  slug: string;
  domain: string | null;
  theme_json: string;
  saas_auto_withdraw_enabled: boolean;
  casino_auto_withdraw_enabled: boolean;
  house_edge_percentage: number;
  withdrawal_fee_type: 'flat' | 'percentage' | 'none';
  withdrawal_fee_amount: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * User model - represents a platform user with hierarchical role
 */
export interface User {
  id: string;
  tenant_id: string | null;
  email: string;
  password_hash: string;
  role: UserRole;
  parent_user_id: string | null;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
  is_active: boolean;
  is_verified: boolean;
  // Premium/Engagement fields
  vip_level: number;
  experience_points: number;
  two_factor_enabled: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * User with computed hierarchy depth (useful for permission checks)
 */
export interface UserWithDepth extends User {
  hierarchy_depth: number;
}

/**
 * Cash Wallet model - fiat wallet for each user
 */
export interface CashWallet {
  id: string;
  user_id: string;
  balance_cents: number;
  created_at: string;
  updated_at: string;
}

/**
 * Cash Ledger entry - immutable record of cash movements
 * NOTE: Strict top-down flow enforced - no upward transfers
 */
export interface CashLedgerEntry {
  id: string;
  transaction_id: string;
  from_user_id: string;
  to_user_id: string;
  amount_cents: number;
  cash_transaction_type: CashTransactionType;
  from_balance_after_cents: number;
  to_balance_after_cents: number;
  created_at: string;
}

/**
 * Crypto Wallet model - custodial cryptocurrency wallet
 * Supports ERC-20 tokens (ETH, USDC, USDT)
 */
export interface CryptoWallet {
  id: string;
  user_id: string;
  token_type: TokenType;
  wallet_address: string;
  balance: number;
  created_at: string;
  updated_at: string;
}

/**
 * Crypto Ledger entry - immutable record of crypto movements
 */
export interface CryptoLedgerEntry {
  id: string;
  transaction_id: string;
  from_user_id: string;
  to_user_id: string;
  token_type: TokenType;
  amount: number;
  crypto_transaction_type: CryptoTransactionType;
  from_balance_after: number;
  to_balance_after: number;
  tx_hash: string | null;
  created_at: string;
}

/**
 * Transaction model - unified ledger for all financial operations
 * SaaS Owner revenue cuts are captured via saas_revenue_cents
 */
export interface Transaction {
  id: string;
  tenant_id: string | null;
  user_id: string;
  category: TransactionCategory;
  amount_cents: number | null;
  amount_crypto: number | null;
  token_type: TokenType | null;
  direction: TransactionDirection;
  payment_method: PaymentMethod | null;
  status: TransactionStatus;
  auto_process_eligible: boolean;
  saas_revenue_cents: number | null;
  saas_revenue_percentage: number | null;
  withdrawal_fee_cents: number | null;
  player_loss_cents: number | null;
  tx_hash: string | null;
  block_number: number | null;
  reference: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
}

/**
 * Manual Withdrawal Queue entry
 * Withdrawals route here when auto_withdraw is disabled at either level
 */
export interface ManualWithdrawalQueueEntry {
  id: string;
  transaction_id: string;
  reason: ManualQueueReason;
  status: ManualQueueStatus;
  assigned_to: string | null;
  processor_notes: string | null;
  created_at: string;
  updated_at: string;
  processed_at: string | null;
}

/**
 * Bet model - records all bet placements
 * Complex game-specific fields stored in separate tables
 */
export interface Bet {
  id: string;
  user_id: string;
  tenant_id: string;
  game_type: string;
  bet_amount_cents: number;
  potential_payout_cents: number;
  status: BetStatus;
  result_amount_cents: number | null;
  game_round_id: string | null;
  odds: number | null;
  market: string | null;
  created_at: string;
  settled_at: string | null;
}

/**
 * Game Result model - stores settled game rounds
 */
export interface GameResult {
  id: string;
  tenant_id: string;
  game_type: string;
  game_round_id: string;
  result_data: string;
  total_bet_amount_cents: number;
  total_payout_amount_cents: number;
  total_player_loss_cents: number;
  created_at: string;
  settled_at: string;
}

/**
 * API Key model - for multi-account provisioning
 * Stores Cloudflare API keys for tenant deployment
 */
export interface ApiKey {
  id: string;
  tenant_id: string | null;
  created_by_user_id: string;
  key_type: ApiKeyType;
  key_label: string;
  cf_account_id: string | null;
  is_active: boolean;
  created_at: string;
  expires_at: string | null;
  last_used_at: string | null;
}

/**
 * Session model - lightweight session tracking
 * Actual session storage in Cloudflare KV
 */
export interface Session {
  id: string;
  user_id: string;
  user_agent: string | null;
  ip_address: string | null;
  is_active: boolean;
  created_at: string;
  expires_at: string;
  last_activity_at: string;
}

/**
 * Audit Log entry - comprehensive audit trail
 */
export interface AuditLogEntry {
  id: string;
  user_id: string | null;
  action: string;
  resource_type: string;
  resource_id: string | null;
  ip_address: string | null;
  user_agent: string | null;
  request_path: string | null;
  request_method: string | null;
  old_values: string | null;
  new_values: string | null;
  created_at: string;
}

// =============================================================================
// ENGAGEMENT & LOYALTY TYPES
// =============================================================================

/**
 * VIP Level configuration
 */
export interface VipLevelConfig {
  level: number;
  xp_required: number;
  title: string;
  benefits_json: Record<string, unknown>;
  icon_url: string | null;
}

/**
 * User's VIP status with progress to next level
 */
export interface VipStatus {
  user_id: string;
  current_level: number;
  current_title: string;
  experience_points: number;
  next_level_xp: number;           // XP required for next level
  xp_progress_percent: number;     // 0-100
  benefits: Record<string, unknown>;
}

/**
 * Bonus reward record
 */
export interface BonusReward {
  id: string;
  user_id: string;
  tenant_id: string;
  reward_type: 'xp_gain' | 'level_up' | 'deposit_bonus' | 'loss_cashback' | 'reload_bonus' | 'referral_bonus' | 'achievement';
  amount_cents: number;
  xp_awarded: number;
  description: string | null;
  reference_type: string | null;
  reference_id: string | null;
  created_at: string;
}

/**
 * Excitement event for live activity feed
 */
export interface ExcitementEvent {
  id: string;
  tenant_id: string;
  event_type: 'big_win' | 'high_stakes_bet' | 'vip_milestone' | 'jackpot_hit' | 'large_deposit' | 'first_win';
  user_display_name: string;
  user_id: string;
  event_data_json: Record<string, unknown>;
  excitement_score: number;
  created_at: string;
}

/**
 * Live activity feed response
 */
export interface LiveActivityResponse {
  events: ExcitementEvent[];
  pagination: {
    page: number;
    page_size: number;
    total_count: number;
  };
}

/**
 * Leaderboard entry
 */
export interface LeaderboardEntry {
  rank: number;
  user_id: string;
  display_name: string;
  value: number;              // Metric value (wins, xp, deposits, etc.)
  avatar_url: string | null;
}

/**
 * Leaderboard response
 */
export interface LeaderboardResponse {
  board_type: string;
  period: string;
  entries: LeaderboardEntry[];
  last_updated: string;
}

// =============================================================================
// API REQUEST TYPES
// =============================================================================

/**
 * Base request interface with common fields
 */
export interface BaseRequest {
  tenant_id?: string;
  user_id?: string;
}

/**
 * Authentication request
 */
export interface AuthRequest {
  email: string;
  password: string;
}

/**
 * Create tenant request (SaaS Owner operation)
 */
export interface CreateTenantRequest {
  name: string;
  slug: string;
  domain?: string;
  theme_json?: string;
  casino_auto_withdraw_enabled?: boolean;
  house_edge_percentage?: number;
  withdrawal_fee_type?: 'flat' | 'percentage' | 'none';
  withdrawal_fee_amount?: number;
}

/**
 * Update tenant request
 */
export interface UpdateTenantRequest {
  name?: string;
  domain?: string;
  theme_json?: string;
  casino_auto_withdraw_enabled?: boolean;
  house_edge_percentage?: number;
  withdrawal_fee_type?: 'flat' | 'percentage' | 'none';
  withdrawal_fee_amount?: number;
  is_active?: boolean;
}

/**
 * Create user request
 */
export interface CreateUserRequest {
  email: string;
  password: string;
  role: UserRole;
  username: string;
  display_name?: string;
  tenant_id?: string;
  parent_user_id?: string;
}

/**
 * Update user request
 */
export interface UpdateUserRequest {
  email?: string;
  display_name?: string;
  avatar_url?: string;
  is_active?: boolean;
  is_verified?: boolean;
}

/**
 * Deposit request (crypto)
 */
export interface CryptoDepositRequest {
  token_type: TokenType;
  amount: number;
  tx_hash: string;
}

/**
 * Deposit request (fiat/cash)
 */
export interface CashDepositRequest {
  amount_cents: number;
  payment_reference?: string;
}

/**
 * Withdrawal request
 */
export interface WithdrawalRequest {
  token_type?: TokenType;
  amount: number;
  destination_address?: string;
  payment_method: PaymentMethod;
}

/**
 * Transfer request (cash only - top-down flow)
 */
export interface CashTransferRequest {
  to_user_id: string;
  amount_cents: number;
  notes?: string;
}

/**
 * Place bet request
 */
export interface PlaceBetRequest {
  game_type: string;
  bet_amount_cents: number;
  potential_payout_cents: number;
  odds?: number;
  market?: string;
  game_round_id?: string;
}

/**
 * Settle bet request
 */
export interface SettleBetRequest {
  bet_id: string;
  status: BetStatus;
  result_amount_cents?: number;
  game_round_id?: string;
}

/**
 * Create API key request
 */
export interface CreateApiKeyRequest {
  tenant_id?: string;
  key_type: ApiKeyType;
  key_label: string;
  cf_account_id?: string;
  expires_at?: string;
}

/**
 * Manual queue processing request
 */
export interface ProcessManualWithdrawalRequest {
  queue_id: string;
  action: 'approve' | 'reject';
  processor_notes?: string;
}

// =============================================================================
// API RESPONSE TYPES
// =============================================================================

/**
 * Base response wrapper
 */
export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: ApiError;
  timestamp: string;
}

/**
 * API error structure
 */
export interface ApiError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

/**
 * Paginated response wrapper
 */
export interface PaginatedResponse<T> extends ApiResponse<T[]> {
  pagination: {
    page: number;
    page_size: number;
    total_count: number;
    total_pages: number;
  };
}

/**
 * Auth response with token
 */
export interface AuthResponse {
  user: User;
  token: string;
  expires_at: string;
}

/**
 * Tenant response
 */
export interface TenantResponse {
  tenant: Tenant;
}

/**
 * User with wallet balances
 */
export interface UserWithWallets extends User {
  cash_wallet?: CashWallet;
  crypto_wallets?: CryptoWallet[];
}

/**
 * Transaction response with related data
 */
export interface TransactionResponse {
  transaction: Transaction;
  cash_ledger_entry?: CashLedgerEntry;
  crypto_ledger_entry?: CryptoLedgerEntry;
}

/**
 * Balance response
 */
export interface BalanceResponse {
  user_id: string;
  cash_balance_cents: number;
  crypto_balances: {
    token_type: TokenType;
    balance: number;
  }[];
}

// =============================================================================
// RBAC & PERMISSIONS
// =============================================================================

/**
 * Permission definitions
 */
export enum Permission {
  // Tenant management
  TENANT_CREATE = 'tenant:create',
  TENANT_READ = 'tenant:read',
  TENANT_UPDATE = 'tenant:update',
  TENANT_DELETE = 'tenant:delete',
  
  // User management
  USER_CREATE = 'user:create',
  USER_READ = 'user:read',
  USER_UPDATE = 'user:update',
  USER_DELETE = 'user:delete',
  
  // Wallet operations
  WALLET_DEPOSIT = 'wallet:deposit',
  WALLET_WITHDRAW = 'wallet:withdraw',
  WALLET_TRANSFER = 'wallet:transfer',
  WALLET_VIEW = 'wallet:view',
  WALLET_VIEW_ALL = 'wallet:view:all',
  
  // Transaction operations
  TRANSACTION_CREATE = 'transaction:create',
  TRANSACTION_READ = 'transaction:read',
  TRANSACTION_UPDATE = 'transaction:update',
  TRANSACTION_CANCEL = 'transaction:cancel',
  
  // Bet operations
  BET_PLACE = 'bet:place',
  BET_SETTLE = 'bet:settle',
  BET_READ = 'bet:read',
  
  // Manual queue operations
  QUEUE_PROCESS = 'queue:process',
  QUEUE_READ = 'queue:read',
  
  // API key management
  API_KEY_CREATE = 'api_key:create',
  API_KEY_READ = 'api_key:read',
  API_KEY_REVOKE = 'api_key:revoke',
  
  // Audit log
  AUDIT_READ = 'audit:read',
  
  // Admin operations
  ADMIN_ALL = 'admin:all'
}

/**
 * Role to permissions mapping
 * Each role inherits all permissions from lower roles
 */
export const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  [UserRole.SAAS_OWNER]: [
    Permission.ADMIN_ALL,
    Permission.TENANT_CREATE,
    Permission.TENANT_READ,
    Permission.TENANT_UPDATE,
    Permission.TENANT_DELETE,
    Permission.USER_CREATE,
    Permission.USER_READ,
    Permission.USER_UPDATE,
    Permission.USER_DELETE,
    Permission.WALLET_DEPOSIT,
    Permission.WALLET_WITHDRAW,
    Permission.WALLET_TRANSFER,
    Permission.WALLET_VIEW,
    Permission.WALLET_VIEW_ALL,
    Permission.TRANSACTION_CREATE,
    Permission.TRANSACTION_READ,
    Permission.TRANSACTION_UPDATE,
    Permission.TRANSACTION_CANCEL,
    Permission.BET_PLACE,
    Permission.BET_SETTLE,
    Permission.BET_READ,
    Permission.QUEUE_PROCESS,
    Permission.QUEUE_READ,
    Permission.API_KEY_CREATE,
    Permission.API_KEY_READ,
    Permission.API_KEY_REVOKE,
    Permission.AUDIT_READ
  ],
  [UserRole.CASINO_MANAGER]: [
    Permission.TENANT_READ,
    Permission.TENANT_UPDATE,
    Permission.USER_CREATE,
    Permission.USER_READ,
    Permission.USER_UPDATE,
    Permission.WALLET_DEPOSIT,
    Permission.WALLET_WITHDRAW,
    Permission.WALLET_TRANSFER,
    Permission.WALLET_VIEW,
    Permission.WALLET_VIEW_ALL,
    Permission.TRANSACTION_CREATE,
    Permission.TRANSACTION_READ,
    Permission.TRANSACTION_UPDATE,
    Permission.BET_PLACE,
    Permission.BET_SETTLE,
    Permission.BET_READ,
    Permission.QUEUE_READ,
    Permission.AUDIT_READ
  ],
  [UserRole.MANAGER]: [
    Permission.TENANT_READ,
    Permission.USER_CREATE,
    Permission.USER_READ,
    Permission.USER_UPDATE,
    Permission.WALLET_TRANSFER,
    Permission.WALLET_VIEW,
    Permission.TRANSACTION_READ,
    Permission.BET_PLACE,
    Permission.BET_READ,
    Permission.QUEUE_READ
  ],
  [UserRole.AGENT]: [
    Permission.TENANT_READ,
    Permission.USER_READ,
    Permission.WALLET_TRANSFER,
    Permission.WALLET_VIEW,
    Permission.TRANSACTION_READ,
    Permission.BET_READ
  ],
  [UserRole.PLAYER]: [
    Permission.TENANT_READ,
    Permission.WALLET_DEPOSIT,
    Permission.WALLET_WITHDRAW,
    Permission.WALLET_VIEW,
    Permission.TRANSACTION_CREATE,
    Permission.TRANSACTION_READ,
    Permission.BET_PLACE,
    Permission.BET_READ
  ]
};

/**
 * Check if a role has a specific permission
 */
export function hasPermission(role: UserRole, permission: Permission): boolean {
  const permissions = ROLE_PERMISSIONS[role];
  return permissions?.includes(permission) ?? false;
}

/**
 * Check if a role can manage another role (hierarchical check)
 */
export function canManageRole(managerRole: UserRole, targetRole: UserRole): boolean {
  const hierarchy: UserRole[] = [
    UserRole.SAAS_OWNER,
    UserRole.CASINO_MANAGER,
    UserRole.MANAGER,
    UserRole.AGENT,
    UserRole.PLAYER
  ];
  
  const managerIndex = hierarchy.indexOf(managerRole);
  const targetIndex = hierarchy.indexOf(targetRole);
  
  // Higher index = lower in hierarchy
  // Manager can only manage roles below them
  return managerIndex < targetIndex;
}

// =============================================================================
// CONFIGURATION TYPES
// =============================================================================

/**
 * Cloudflare environment bindings
 */
export interface CloudflareEnv {
  DB: D1Database;
  KV: KVNamespace;
  SESSIONS: KVNamespace;
}

/**
 * Multi-account provisioning configuration
 */
export interface MultiAccountConfig {
  mode: 'saas_owner' | 'tenant_cf_account';
  cf_account_id?: string;
  cf_api_key?: string;
}

/**
 * Withdrawal automation configuration
 */
export interface WithdrawalAutoConfig {
  saas_auto_withdraw_enabled: boolean;
  casino_auto_withdraw_enabled: boolean;
  minimum_auto_amount?: number;
  maximum_auto_amount?: number;
}

/**
 * SaaS Owner revenue configuration
 */
export interface RevenueConfig {
  house_edge_percentage: number;
  withdrawal_fee_type: 'flat' | 'percentage' | 'none';
  withdrawal_fee_amount: number;
}

// =============================================================================
// CONTEXT TYPES
// =============================================================================

/**
 * Authenticated request context
 */
export interface AuthContext {
  user: User;
  tenant: Tenant | null;
  permissions: Permission[];
  session_id: string;
}

/**
 * Request validation result
 */
export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

// =============================================================================
// UTILITY TYPES
// =============================================================================

/**
 * Type for creating new entities (without server-generated fields)
 */
export type CreateEntity<T> = Omit<T, 'id' | 'created_at' | 'updated_at'>;

/**
 * Type for updating entities (all fields optional)
 */
export type UpdateEntity<T> = Partial<Omit<T, 'id' | 'created_at'>>;

/**
 * Type for JSON serialization
 */
export type JSONValue = string | number | boolean | null | JSONObject | JSONValue[];
export type JSONObject = { [key: string]: JSONValue };

/**
 * Database row type (for D1 queries)
 */
export type D1Row = Record<string, unknown>;
export type D1Result<T> = { results: T[]; success: boolean };